import * as React from 'react';

/**
 * Styled components.
 */
import {
  StyledLabel,
  RequiredStar,
  StyledItem,
  StyledItemProps
} from './Item.style';
import LabelTooltip from './LabelTooltip';

export interface IItemProps extends StyledItemProps {
  /**
   * Display value for the form item.
   */
  label: string;

  /**
   * ID for the label to be rendered for accessibility purposes
   */
  labelId?: string;

  /**
   * The `id` of the child input element. Required for screen readers
   * to associate this component's `id` with the `id` of the
   * input component.
   */
  htmlFor: string;

  /**
   * Whether the component is disabled.
   *
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether this form item is a required field.
   *
   * @default false
   */
  required?: boolean;

  /**
   * Whether the form element should have its label
   * rendered inline with the input. Needed for checkboxes
   * and radio buttons.
   *
   * @default false
   */
  inlineLabel?: boolean;

  /**
   * If provided with text value, an icon with tooltip will appear next to
   * the label
   *
   * @default undefined
   */
  tooltip?: string;

  children?: React.ReactNode;
}

/**
 * Returns a component meant to wrap input elements such as text input, text-area
 * checkboxes, dropdowns and radio groups. Renders a label with added aria attributes
 * for accessibility. This component will also drill disabled, required and formId
 * props to it's children.
 */
const Item: React.FC<IItemProps> = ({
  label,
  children,
  required,
  disabled,
  labelId,
  htmlFor,
  inlineLabel = false,
  tooltip,
  fontSize,
  ...rest
}) => {
  /**
   * Drill props into children -- spread child props last to override any props
   * the consumer wants to override
   */
  const possibleProps = [{ disabled }, { required }];
  const builtProps = possibleProps.reduce((prev, cur) => {
    if (Object.values(cur)[0] === undefined) {
      return { ...prev };
    }
    return { ...cur, ...prev };
  }, {});
  const ChildrenWithProps = React.Children.map(
    children,
    (child: React.ReactElement) => {
      return React.cloneElement(child, {
        ...builtProps,
        ...child.props
      });
    }
  );

  const isCheckboxOrRadio = React.Children.toArray(children).some(child =>
    ['Form.Checkbox', 'Form.Radiobox'].includes((child as any).type.displayName)
  );

  return (
    <StyledItem
      width={inlineLabel ? 'auto' : 1}
      flexDirection={inlineLabel ? 'row' : 'column'}
      alignItems={inlineLabel ? 'center' : 'flex-start'}
      disabled={disabled}
      data-formitem
      {...rest}
    >
      <StyledLabel
        color={
          disabled ? 'form.text.label.disabled' : 'form.text.label.enabled'
        }
        fontSize={fontSize}
        id={labelId}
        htmlFor={htmlFor}
        kind="label"
        order={inlineLabel ? 2 : 0}
        mt={1}
        mb={1}
        isCheckboxOrRadio={isCheckboxOrRadio}
      >
        {label}{' '}
        {required && (
          <RequiredStar color="state.danger" fontSize={0}>
            *
          </RequiredStar>
        )}
        {tooltip && (
          <LabelTooltip id={`tooltip-${htmlFor}`}>{tooltip}</LabelTooltip>
        )}
      </StyledLabel>
      {ChildrenWithProps}
    </StyledItem>
  );
};

export default Item;
